import React, { useEffect, useState } from 'react';

import { Person, Business } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import * as profileSelectors from '../../profile/selectors';
import * as actions from '../actions';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: '28px', // cápsula elegante
    padding: '6px',
    display: 'inline-flex',
    gap: '8px', // espacio entre botones

    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        border: 'none',
        borderRadius: '24px', // redondeado fuerte en cada botón
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        padding: '6px 16px', // tamaño small
        minHeight: '36px',

        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },

        '&:not(:first-of-type)': {
            borderRadius: '24px',
        },
        '&:first-of-type': {
            borderRadius: '24px',
        },
    },
}));

const PreferenceToggle = ({
    isFetching,
    presetScope = 'USER',
    savePreference,
}) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState(presetScope);

    useEffect(() => {
        setScope(presetScope);
    }, [presetScope]);

    const handleChange = (event, newValue) => {
        if (newValue !== null) {
            setScope(newValue);
            savePreference(newValue);
        }
    };

    if (isFetching) return null;

    return (
        <Box
            display="flex"
            flexDirection="column"
            mb={2}
            gap={2}
            sx={{ width: 'fit-content' }}
        >
            <Typography fontWeight="bold" variant="body2" pl={2}>
                {t('Scope')}
            </Typography>

            <StyledToggleButtonGroup
                value={scope}
                exclusive
                onChange={handleChange}
                aria-label="scope preference"
            >
                <ToggleButton value="USER" aria-label="Usuario">
                    <Person sx={{ mr: 1, fontSize: 20 }} />
                    {t('User')}
                </ToggleButton>

                <ToggleButton value="COMPANY" aria-label="Empresa">
                    <Business sx={{ mr: 1, fontSize: 20 }} />
                    {t('Company')}
                </ToggleButton>
            </StyledToggleButtonGroup>
        </Box>
    );
};

PreferenceToggle.propTypes = {
    isFetching: PropTypes.bool,
    presetScope: PropTypes.oneOf(['USER', 'COMPANY']),
    savePreference: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
    presetScope: profileSelectors.getPresetScope,
    isFetching: profileSelectors.getIsFetching,
});

const mapDispatchToProps = (dispatch) => ({
    savePreference: (value) => dispatch(actions.savePreference(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PreferenceToggle);
